import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as tus from 'tus-js-client';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Users, Video, TrendingUp, Edit, Trash2, Ban, CheckCircle, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { API, BACKEND_URL } from '@/config';

// Helper function to safely extract error messages
const getErrorMessage = (error, defaultMessage = 'Ha ocurrido un error') => {
  if (!error) return defaultMessage;
  
  // Try to get from response.data
  if (error.response && error.response.data) {
    const data = error.response.data;
    
    // If data is a string, return it
    if (typeof data === 'string') {
      return data;
    }
    
    // If data has detail and it's a string, return it
    if (data.detail) {
      if (typeof data.detail === 'string') {
        return data.detail;
      }
      // If detail is an array (FastAPI validation errors)
      if (Array.isArray(data.detail)) {
        return data.detail
          .map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return err.msg;
            return 'Error de validación';
          })
          .join(', ');
      }
    }
    
    // If data has message and it's a string, return it
    if (data.message && typeof data.message === 'string') {
      return data.message;
    }
  }
  
  // Try to get from error.message
  if (error.message && typeof error.message === 'string') {
    return error.message;
  }
  
  // Return default message
  return defaultMessage;
};

// Maps a user's subscription_status to display badge text + Tailwind classes.
const getSubscriptionBadge = (status) => {
  switch (status) {
    case 'active':
      return { label: 'Activa', className: 'bg-green-100 text-green-800' };
    case 'cancelled':
      return { label: 'Cancelada', className: 'bg-amber-100 text-amber-800' };
    case 'past_due':
      return { label: 'Pago vencido', className: 'bg-red-100 text-red-800' };
    default:
      return { label: 'Sin suscripción', className: 'bg-gray-100 text-gray-800' };
  }
};

const formatSubscriptionDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-ES', { timeZone: 'UTC' });
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [metrics, setMetrics] = useState({
    total_users: 0,
    total_videos: 0
  });
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [plinOrders, setPlinOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [editUserModal, setEditUserModal] = useState(null);
  const [videoModal, setVideoModal] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    if (token) {
      fetchAdminData();
    }
  }, [user, token]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      if (!token) {
        console.error('No token available');
        toast.error('Error de autenticación');
        navigate('/auth/login');
        return;
      }
      
      const headers = { 'Authorization': `Bearer ${token}` };

      const results = await Promise.allSettled([
        axios.get(`${API}/admin/metrics`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/videos`, { headers }),
        axios.get(`${API}/admin/plin-orders`, { headers })
      ]);

      // Un fallo de auth redirige al login; cualquier otro fallo solo deja el valor por defecto de esa sección.
      const authFailure = results.find(
        (r) => r.status === 'rejected' && (r.reason?.response?.status === 401 || r.reason?.response?.status === 403)
      );
      if (authFailure) throw authFailure.reason;

      const [metricsRes, usersRes, videosRes, plinOrdersRes] = results;
      if (metricsRes.status === 'fulfilled') {
        setMetrics(metricsRes.value.data || {
          total_users: 0,
          total_videos: 0
        });
      }
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data || []);
      if (videosRes.status === 'fulfilled') setVideos(videosRes.value.data || []);
      if (plinOrdersRes.status === 'fulfilled') setPlinOrders(plinOrdersRes.value.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error(getErrorMessage(error, 'Error al cargar datos administrativos'));
      setLoading(false);
      
      // If unauthorized, redirect to login
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/auth/login');
      }
    }
  };

  const handleBlockUser = async (userId, isBlocked) => {
    try {
      const endpoint = isBlocked ? 'unblock' : 'block';
      await axios.put(`${API}/admin/users/${userId}/${endpoint}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(isBlocked ? 'Usuario desbloqueado' : 'Usuario bloqueado');
      fetchAdminData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cambiar estado del usuario'));
    }
  };

  const handleConfirmPlinOrder = async (orderId) => {
    try {
      const response = await axios.post(`${API}/admin/plin-orders/${orderId}/confirm`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Pedido confirmado');
      fetchAdminData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al confirmar el pedido'));
    }
  };

  const handleRejectPlinOrder = async (orderId) => {
    try {
      await axios.post(`${API}/admin/plin-orders/${orderId}/reject`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Pedido rechazado');
      fetchAdminData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al rechazar el pedido'));
    }
  };

  const handleViewVoucher = async (orderId) => {
    const win = window.open('', '_blank');
    try {
      const response = await axios.get(`${API}/admin/plin-orders/${orderId}/voucher`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (win) {
        const img = win.document.createElement('img');
        img.src = response.data.voucher_image;
        img.style.maxWidth = '100%';
        win.document.body.appendChild(img);
      }
    } catch (error) {
      if (win) win.close();
      toast.error(getErrorMessage(error, 'Error al cargar el voucher'));
    }
  };

  const handleUpdateUser = async (userId, data) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, data, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Usuario actualizado');
      setEditUserModal(null);
      fetchAdminData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar usuario'));
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!confirm('¿Estás seguro de eliminar este video?')) return;
    
    try {
      await axios.delete(`${API}/admin/videos/${videoId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Video eliminado');
      fetchAdminData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar video'));
    }
  };

  const handleSaveVideo = async (videoData) => {
    try {
      if (videoData.id) {
        // Update existing
        await axios.put(`${API}/admin/videos/${videoData.id}`, videoData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Video actualizado');
      } else {
        // Create new
        await axios.post(`${API}/admin/videos`, videoData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Video creado');
      }
      setVideoModal(null);
      fetchAdminData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar video'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-5xl font-bold text-amber-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Panel de Administración
            </h1>
            <p className="text-xl text-gray-700">
              Gestión completa de Nazca360
            </p>
          </div>

          {/* Metrics Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 text-amber-700" />
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-amber-900">{metrics.total_users}</p>
              <p className="text-sm text-gray-600">Total Usuarios</p>
            </div>

            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between mb-2">
                <Video className="w-8 h-8 text-amber-700" />
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-amber-900">{metrics.total_videos}</p>
              <p className="text-sm text-gray-600">Total Videos</p>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="users" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="users">Usuarios</TabsTrigger>
              <TabsTrigger value="videos">Videos</TabsTrigger>
              <TabsTrigger value="plin">Plin</TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users">
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-amber-900 mb-6">Gestión de Usuarios</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-amber-200">
                        <th className="text-left py-3 px-4 text-amber-900">Nombre</th>
                        <th className="text-left py-3 px-4 text-amber-900">Email</th>
                        <th className="text-left py-3 px-4 text-amber-900">Estado</th>
                        <th className="text-left py-3 px-4 text-amber-900">Suscripción</th>
                        <th className="text-left py-3 px-4 text-amber-900">Desde</th>
                        <th className="text-left py-3 px-4 text-amber-900">Hasta</th>
                        <th className="text-left py-3 px-4 text-amber-900">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-b border-amber-100 hover:bg-amber-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium">{u.name}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                u.role === 'admin'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {u.role}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm">{u.email}</td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              u.is_blocked
                                ? 'bg-red-100 text-red-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {u.is_blocked ? 'Bloqueado' : 'Activo'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {(() => {
                              const badge = getSubscriptionBadge(u.subscription_status);
                              return (
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.className}`}>
                                  {badge.label}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="py-3 px-4 text-sm">{formatSubscriptionDate(u.current_period_start)}</td>
                          <td className="py-3 px-4 text-sm">{formatSubscriptionDate(u.current_period_end)}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditUserModal(u)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleBlockUser(u.user_id, u.is_blocked)}
                                className={u.is_blocked ? 'text-green-600' : 'text-red-600'}
                                disabled={u.user_id === user.user_id}
                              >
                                {u.is_blocked ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos">
              <div className="glass rounded-2xl p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-amber-900">Gestión de Videos</h2>
                  <Button onClick={() => setVideoModal({})}>
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Video
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videos.map((video) => (
                    <div key={video.id} className="border-2 border-amber-200 rounded-xl p-4 hover:shadow-lg transition">
                      <div className="relative mb-3">
                        {video.thumbnail_url ? (
                          <img
                            src={video.thumbnail_url?.startsWith('/api') ? `${BACKEND_URL}${video.thumbnail_url}` : video.thumbnail_url}
                            alt={video.title}
                            className="w-full h-40 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-full h-40 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Video className="w-12 h-12 text-amber-400" />
                          </div>
                        )}
                        {video.is_premium && (
                          <span className="absolute top-2 right-2 bg-amber-500 text-white px-2 py-1 rounded text-xs font-bold">
                            PREMIUM
                          </span>
                        )}
                        {video.is_demo && (
                          <span className="absolute top-2 left-2 bg-green-600 text-white px-2 py-1 rounded text-xs font-bold">
                            DEMO
                          </span>
                        )}
                      </div>
                      <h3 className="font-bold text-amber-900 mb-1">{video.title}</h3>
                      <div className="flex items-center gap-2 mb-3">
                        <p className="text-sm text-gray-600">{video.category}</p>
                        {video.language && (
                          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium uppercase">
                            {video.language}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setVideoModal(video)}
                          className="flex-1"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteVideo(video.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Plin Tab */}
            <TabsContent value="plin">
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-amber-900 mb-6">Pagos por Plin</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-amber-200">
                        <th className="text-left py-3 px-4 text-amber-900">Usuario</th>
                        <th className="text-left py-3 px-4 text-amber-900">Tipo</th>
                        <th className="text-left py-3 px-4 text-amber-900">Paquete</th>
                        <th className="text-left py-3 px-4 text-amber-900">Monto</th>
                        <th className="text-left py-3 px-4 text-amber-900">Fecha</th>
                        <th className="text-left py-3 px-4 text-amber-900">Estado</th>
                        <th className="text-left py-3 px-4 text-amber-900">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plinOrders.map((order) => (
                        <tr key={order.id} className="border-b border-amber-100 hover:bg-amber-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium">{order.user_name}</p>
                              <p className="text-xs text-gray-500">{order.user_email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {order.type === 'subscription' ? 'Suscripción' : 'Minutos'}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {order.type === 'subscription' ? (
                              <button onClick={() => handleViewVoucher(order.id)} className="text-blue-600 hover:text-blue-800 underline text-xs">
                                Ver comprobante
                              </button>
                            ) : (
                              `${order.minutes} min`
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm">S/ {order.price_pen}</td>
                          <td className="py-3 px-4 text-sm">{new Date(order.created_at).toLocaleString('es-ES', { timeZone: 'UTC' })}</td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              order.status === 'confirmed'
                                ? 'bg-green-100 text-green-800'
                                : order.status === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}>
                              {order.status === 'confirmed' ? 'Confirmado' : order.status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {order.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleConfirmPlinOrder(order.id)}
                                  className="text-green-600 hover:text-green-800"
                                >
                                  Confirmar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRejectPlinOrder(order.id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Rechazar
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Edit User Modal */}
      {editUserModal && (
        <EditUserModal
          user={editUserModal}
          onClose={() => setEditUserModal(null)}
          onSave={handleUpdateUser}
        />
      )}

      {/* Video Modal */}
      {videoModal !== null && (
        <VideoModal
          video={videoModal}
          onClose={() => setVideoModal(null)}
          onSave={handleSaveVideo}
        />
      )}
    </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    role: user.role || 'user',
    is_blocked: user.is_blocked || false
  });

  const handleSave = () => {
    onSave(user.user_id, formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-amber-900">Editar Usuario</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rol</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="user">Usuario</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
              <select
                value={formData.is_blocked ? 'blocked' : 'active'}
                onChange={(e) => setFormData({...formData, is_blocked: e.target.value === 'blocked'})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="active">Activo</option>
                <option value="blocked">Bloqueado</option>
              </select>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mt-4">
            <p className="text-sm text-gray-600">
              <strong>user_id:</strong> {user.user_id}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Creado:</strong> {user.created_at ? new Date(user.created_at).toLocaleDateString('es-PE') : 'N/A'}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Proveedor Auth:</strong> {user.oauth_provider || 'Email/Password'}
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
          <Button onClick={onClose} variant="outline" className="flex-1">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 btn-peru"
          >
            Guardar Cambios
          </Button>
        </div>
      </div>
    </div>
  );
};

// Video Modal Component
const VideoModal = ({ video, onClose, onSave }) => {
  const { token } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    id: video.id || null,
    title: video.title || '',
    description: video.description || '',
    url: video.url || '',
    thumbnail_url: video.thumbnail_url || '',
    category: video.category || 'nasca',
    language: video.language || 'es',
    duration: video.duration || '',
    cultural_tags: video.cultural_tags?.join(', ') || '',
    stereo_format: video.stereo_format || 'mono',
    is_premium: video.is_premium ?? true,
    is_demo: video.is_demo ?? false
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    video: 0,
    thumbnail: 0
  });

  const handleFileUpload = async (file, fieldName) => {
    if (!file) return;
    
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const fileSizeGB = (file.size / (1024 * 1024 * 1024)).toFixed(2);
    
    // Video files: direct resumable upload to Cloudflare Stream (TUS)
    if (fieldName === 'video') {
      try {
        setUploading(true);
        setUploadProgress(prev => ({ ...prev, [fieldName]: 1 }));
        
        const sizeDisplay = file.size > 1024*1024*1024 ? fileSizeGB + 'GB' : fileSizeMB + 'MB';
        toast.info(`Subiendo video (${sizeDisplay})...`);

        // Direct upload to Cloudflare Stream via TUS (resumable, any size)
        let mediaId = null;
        let tusUpload = null;
        await new Promise((resolve, reject) => {
          const upload = new tus.Upload(file, {
            endpoint: `${API}/stream/tus`,
            chunkSize: 50 * 1024 * 1024,
            retryDelays: [0, 3000, 10000, 30000],
            removeFingerprintOnSuccess: true,
            metadata: { name: file.name, filetype: file.type || 'video/mp4' },
            onBeforeRequest: (req) => {
              // Solo nuestro endpoint recibe el token; las subidas a Cloudflare no.
              if (req.getURL().startsWith(`${API}/stream/tus`)) {
                req.setHeader('Authorization', `Bearer ${token}`);
              }
            },
            onAfterResponse: (req, res) => {
              const id = res.getHeader('stream-media-id');
              if (id) mediaId = id;
            },
            onProgress: (sent, total) => {
              const percentage = Math.round((sent / total) * 100);
              setUploadProgress(prev => ({ ...prev, [fieldName]: Math.max(1, Math.min(percentage, 99)) }));
            },
            onError: reject,
            onSuccess: resolve,
          });
          tusUpload = upload;
          upload.start();
        });

        if (!mediaId && tusUpload?.url) {
          mediaId = tusUpload.url.split('?')[0].split('/').filter(Boolean).pop() || null;
        }
        if (!mediaId) throw new Error('Cloudflare no devolvió el id del video');
        setUploadProgress(prev => ({ ...prev, [fieldName]: 100 }));
        setFormData(prev => ({ ...prev, url: `stream://${mediaId}` }));
        toast.success('¡Video subido a Cloudflare Stream!');
        
        setUploading(false);
        setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
        
      } catch (error) {
        console.error('Video upload error:', error);
        const errorMsg = error.response?.data?.detail || error.message || 'Error al subir video';
        toast.error(`Error: ${errorMsg}`);
        setUploading(false);
        setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
      }
      return;
    }
    
    // For thumbnails and other images - upload to Cloudflare Images (persistent across redeploys)
    try {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API}/admin/images/upload`, uploadFormData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [fieldName]: percentCompleted }));
        }
      });

      setFormData(prev => ({
        ...prev,
        thumbnail_url: response.data.signed_url || response.data.url  // signed for immediate preview; backend re-signs on read
      }));
      
      toast.success('Imagen subida a Cloudflare Images ✓');
      setUploading(false);
      setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(getErrorMessage(error, 'Error al subir imagen'));
      setUploading(false);
      setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
    }
  };

  const handleSubmit = () => {
    if (!formData.title || !formData.description || !formData.url) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    const dataToSave = {
      ...formData,
      cultural_tags: formData.cultural_tags.split(',').map(t => t.trim()).filter(t => t)
    };
    onSave(dataToSave);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 my-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-amber-900">
            {video.id ? 'Editar Video' : 'Agregar Video'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Título*</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Descripción*</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              rows="3"
              required
            />
          </div>

          {/* Video Original Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Video Original (Alta Calidad)*
            </label>
            <p className="text-xs text-green-600 mb-2">
              🎬 Cloudflare Stream: Transcoding automático • CDN global • Streaming adaptativo HLS
            </p>
            
            <div className="space-y-3">
              {/* Option 1: Upload file */}
              <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                <p className="text-xs font-medium text-gray-600 mb-2">Opción 1: Subir archivo</p>
                <input
                  type="file"
                  accept="video/mp4,video/webm"
                  onChange={(e) => handleFileUpload(e.target.files[0], 'video')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white"
                  disabled={uploading}
                />
              </div>
              
              {/* Option 2: Paste Cloudflare Video ID */}
              <div className="p-3 border border-blue-200 rounded-lg bg-blue-50">
                <p className="text-xs font-medium text-blue-600 mb-2">
                  Opción 2: Pegar Video ID de Cloudflare (si ya subiste directo a Cloudflare)
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Ej: a1b2c3d4e5f6..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    id="cloudflare-video-id"
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const videoId = document.getElementById('cloudflare-video-id').value.trim();
                      if (videoId) {
                        setFormData(prev => ({ ...prev, url: `stream://${videoId}` }));
                        toast.success('Video ID vinculado correctamente');
                      } else {
                        toast.error('Ingresa un Video ID válido');
                      }
                    }}
                    disabled={uploading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4"
                  >
                    Vincular
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Encuentra el Video ID en tu panel de Cloudflare Stream
                </p>
              </div>
              
              {uploading && uploadProgress.video > 0 && (
                <div className="space-y-1">
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="h-3 rounded-full transition-all duration-300 bg-orange-500"
                      style={{ width: `${uploadProgress.video}%` }}
                    ></div>
                  </div>
                  <p className="text-sm font-medium text-orange-600">
                    🎬 Procesando y subiendo a Cloudflare Stream... {uploadProgress.video}%
                  </p>
                </div>
              )}
              {formData.url && !uploading && (
                <p className="text-sm text-green-600 font-medium">
                  ✓ Video vinculado: {formData.url.startsWith('stream://') ? `🎬 Cloudflare Stream (${formData.url.replace('stream://', '')})` : 'Local'}
                </p>
              )}
            </div>
          </div>

          {/* Thumbnail Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Miniatura (Imagen - Opcional)
            </label>
            <div className="space-y-3">
              {/* Option 1: Upload file */}
              <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                <p className="text-xs font-medium text-gray-600 mb-2">Opción 1: Subir archivo</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files[0], 'thumbnail')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white"
                  disabled={uploading}
                />
              </div>

              {/* Option 2: Paste Cloudflare Images URL */}
              <div className="p-3 border border-blue-200 rounded-lg bg-blue-50">
                <p className="text-xs font-medium text-blue-600 mb-2">
                  Opción 2: Pegar URL de imagen de Cloudflare (si ya la subiste directo a Cloudflare Images)
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="https://imagedelivery.net/..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    id="cloudflare-thumbnail-url"
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const url = document.getElementById('cloudflare-thumbnail-url').value.trim();
                      if (url) {
                        setFormData(prev => ({ ...prev, thumbnail_url: url }));
                        toast.success('Imagen vinculada correctamente');
                      } else {
                        toast.error('Ingresa una URL válida');
                      }
                    }}
                    disabled={uploading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4"
                  >
                    Vincular
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Copia la URL pública desde tu panel de Cloudflare Images
                </p>
              </div>

              {uploading && uploadProgress.thumbnail > 0 && (
                <div className="space-y-1">
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-amber-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress.thumbnail}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-amber-600 font-medium">
                    Subiendo imagen... {uploadProgress.thumbnail}%
                  </p>
                </div>
              )}
              {formData.thumbnail_url && !uploading && (
                <p className="text-sm text-green-600 font-medium">
                  ✓ Imagen vinculada: {formData.thumbnail_url.includes('imagedelivery.net') ? '☁️ Cloudflare Images' : formData.thumbnail_url.split('/').pop()}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Categoría*</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="nasca">Líneas de Nasca</option>
              <option value="palpa">Líneas de Palpa</option>
              <option value="museum">Museo Virtual</option>
              <option value="archaeological">Sitios Arqueológicos</option>
              <option value="nature">Atractivos Naturales</option>
              <option value="other">Otros</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Idioma*</label>
            <select
              data-testid="video-language-select"
              value={formData.language}
              onChange={(e) => setFormData({...formData, language: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="es">Español</option>
              <option value="en">Inglés</option>
              <option value="ja">Japonés</option>
              <option value="pt">Portugués</option>
              <option value="zh">Chino</option>
              <option value="it">Italiano</option>
              <option value="other">Otros</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Formato de Video 360°</label>
            <select
              value={formData.stereo_format}
              onChange={(e) => setFormData({...formData, stereo_format: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="mono">Monoscópico (Normal)</option>
              <option value="sbs">Estereoscópico Side-by-Side (SBS)</option>
              <option value="tb">Estereoscópico Top-Bottom (TB)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Si el video se ve dividido en dos, selecciona el formato estereoscópico correspondiente.
            </p>
          </div>

          <div className="flex items-center gap-3 p-3 bg-green-50 border-2 border-green-200 rounded-lg">
            <input
              id="is_demo_checkbox"
              data-testid="video-is-demo"
              type="checkbox"
              checked={formData.is_demo}
              onChange={(e) => setFormData({...formData, is_demo: e.target.checked})}
              className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
            />
            <label htmlFor="is_demo_checkbox" className="text-sm font-medium text-green-900 cursor-pointer">
              🎬 Destacar en la página /demos
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Duración (ej: 5:30)</label>
            <input
              type="text"
              value={formData.duration}
              onChange={(e) => setFormData({...formData, duration: e.target.value})}
              placeholder="5:30"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Etiquetas Culturales (separadas por comas)</label>
            <input
              type="text"
              value={formData.cultural_tags}
              onChange={(e) => setFormData({...formData, cultural_tags: e.target.value})}
              placeholder="geoglifo, líneas, colibrí"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900">
              <strong>Nota:</strong> Todo el contenido es de acceso libre — cualquier visitante puede verlo sin cuenta ni pago.
            </p>
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t">
            <Button onClick={onClose} variant="outline" className="flex-1" disabled={uploading}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 btn-peru"
              disabled={!formData.title || !formData.description || !formData.url || uploading}
            >
              {uploading ? 'Subiendo...' : (video.id ? 'Actualizar' : 'Crear')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
